import { OrderQualificationResponses } from '../components/orders/OrderQualificationResponses';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Package,
  Search,
  RefreshCw,
  Eye,
  Truck,
  CheckCircle,
  X,
  ChevronLeft,
  ChevronRight,
  User,
  Building2,
  CreditCard,
  FileText,
  Calendar,
  AlertCircle,
  AlertTriangle,
  Shield,
  MapPin,
  Phone,
  Mail,
  Pill,
  DollarSign,
  Loader2,
  XCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Layers
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { toast } from '../lib/toast';
import { formatDateTime, formatDateForInput } from '../utils/dateUtils';
import { EditAddressModal } from '../components/orders/EditAddressModal';
import { SwitchDeliveryModeModal } from '../components/orders/SwitchDeliveryModeModal';
import { AddItemsModal } from '../components/orders/AddItemsModal';
import { OrderHoldPanel, type OrderHoldTarget } from '../components/orders/OrderHoldPanel';
import { canEditOrderClient } from '../lib/orderEditability';
import { OrderPharmacyBadge } from '../components/pharmacy/PharmacyBadge';
import { itemsWithPeptide } from '../lib/pharmacyRouting';
import { useProductPeptideMap } from '../hooks/useProductPeptideMap';

// Types
interface UnifiedOrder {
  _id: string;
  orderNumber: string;
  orderType: 'patient' | 'bulk';
  status: string;
  subtotal?: number;
  shippingAmount?: number;
  telemedNetworkFee?: number;
  totalAmount: number;
  itemCount: number;
  itemSummary?: Array<{ name: string; quantity: number }>;
  physician: { _id: string; name: string; clinicName?: string; email?: string } | null;
  accountManager?: { _id: string; name: string; companyName?: string; email?: string } | null;
  patient?: { _id: string; name: string } | null;
  fulfillmentType?: 'pickup' | 'bulk_ship' | 'patient_ship';
  shippingDestination?: 'patient' | 'clinic';
  shippingAddress?: { street?: string; street2?: string; city?: string; state?: string; zipCode?: string } | null;
  transmittedAddress?: { street?: string; street2?: string; city?: string; state?: string; zipCode?: string } | null;
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceStatus?: string;
  paymentMethod?: 'patient_pay' | 'physician_billed';
  pharmacySubmissionStatus?: 'not_submitted' | 'partially_submitted' | 'fully_submitted' | 'failed';
  createdAt: string;
  shippedAt?: string;
  deliveredAt?: string;
  trackingInfo?: {
    trackingNumber?: string;
    trackingUrl?: string;
  };
}

interface OrderGroup {
  key: string;
  physicianId: string;
  physicianName: string;
  clinicName?: string;
  physicianEmail?: string;
  amName?: string;
  amCompanyName?: string;
  amEmail?: string;
  date: string;
  orders: UnifiedOrder[];
  totalAmount: number;
  totalItems: number;
  statuses: string[];
  orderTypes: ('patient' | 'bulk')[];
}

interface UnifiedOrderDetail extends UnifiedOrder {
  programAssessments?: import('@bloom/shared').IntakeProgramSnapshot[];
  qualificationResponses?: import('@bloom/shared').ProductQualificationSnapshot[];
  items: Array<{
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    selectedVariants?: Record<string, string>;
  }>;
  // Extended physician info for patient orders
  physician: {
    _id: string;
    name: string;
    clinicName?: string;
    clinicAddress?: {
      street?: string;
      street2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  } | null;
  // Extended patient info for patient orders
  patient?: {
    _id: string;
    name: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
    address?: {
      street?: string;
      street2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  } | null;
  paymentDetails?: {
    method: string;
    status: string;
    amount: number;
    completedAt?: string;
  };
  invoicePaidAt?: string;
  trackingInfo?: {
    carrier?: string;
    trackingNumber?: string;
    trackingUrl?: string;
    addedBy?: {
      _id: string;
      name: string;
    };
    addedAt?: string;
  };
  pharmacySubmission?: {
    status: 'not_submitted' | 'partially_submitted' | 'fully_submitted' | 'failed';
    pharmacyOrderId?: string;  // For patient orders - single order ID
    pharmacyOrderIds?: number[];  // For bulk orders - multiple order IDs
    lastUpdate?: string;
    failureReason?: string;  // Why the pharmacy rejected the order
  };
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceStatus?: string;
  submittedAt?: string;
  paidAt?: string;
}

interface Pagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface OrderCounts {
  patient: number;
  bulk: number;
}

// Status badge component
const StatusBadge: React.FC<{ status: string; orderType: 'patient' | 'bulk' }> = ({ status, orderType: _orderType }) => {
  const getStatusStyle = () => {
    switch (status) {
      case 'draft':
      case 'voided':
        return 'bg-gray-100 text-gray-800';
      case 'pending_approval':
      case 'awaiting_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'submitted':
      case 'paid':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-purple-100 text-purple-800';
      case 'shipped':
        return 'bg-indigo-100 text-indigo-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle()}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

// Flags orders the pharmacy rejected so admins can spot them in the list without
// opening each one. Renders nothing unless the pharmacy submission actually failed.
const PharmacyFailedBadge: React.FC<{ status?: string }> = ({ status }) => {
  if (status !== 'failed') return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
      title="This order was rejected by the pharmacy and did not transmit"
    >
      <AlertTriangle className="w-3 h-3" />
      Pharmacy failed
    </span>
  );
};

// Payment status badge derived from invoice status
const PaymentStatusBadge: React.FC<{ invoiceStatus?: string }> = ({ invoiceStatus }) => {
  const getConfig = () => {
    switch (invoiceStatus) {
      case 'paid':
        return { style: 'bg-green-100 text-green-800', label: 'Paid' };
      case 'overdue':
        return { style: 'bg-red-100 text-red-800', label: 'Overdue' };
      case 'sent':
      case 'unpaid':
        return { style: 'bg-orange-100 text-orange-800', label: 'Unpaid' };
      case 'partial':
        return { style: 'bg-yellow-100 text-yellow-800', label: 'Partial' };
      case 'voided':
        return { style: 'bg-gray-100 text-gray-600', label: 'Voided' };
      case 'refunded':
        return { style: 'bg-gray-100 text-gray-600', label: 'Refunded' };
      default:
        return { style: 'bg-gray-100 text-gray-500', label: 'N/A' };
    }
  };

  const config = getConfig();
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.style}`}>
      {config.label}
    </span>
  );
};

// Order type + destination badge
const OrderTypeBadge: React.FC<{
  orderType: 'patient' | 'bulk';
  fulfillmentType?: 'pickup' | 'bulk_ship' | 'patient_ship';
  shippingDestination?: 'patient' | 'clinic';
}> = ({ orderType, fulfillmentType, shippingDestination }) => {
  // Determine the destination label
  const getDestinationLabel = () => {
    if (orderType === 'patient') {
      return shippingDestination === 'clinic' ? 'To Clinic' : 'To Patient';
    }
    // Bulk orders
    switch (fulfillmentType) {
      case 'pickup':
        return 'Pickup';
      case 'bulk_ship':
        return 'To Clinic';
      case 'patient_ship':
        return 'To Patients';
      default:
        return 'To Clinic';
    }
  };

  const destinationLabel = getDestinationLabel();

  // Color based on order type AND fulfillment type
  const getColors = () => {
    if (orderType === 'patient') {
      return shippingDestination === 'clinic'
        ? { bg: 'bg-purple-100', text: 'text-purple-800' }
        : { bg: 'bg-emerald-100', text: 'text-emerald-800' };
    }
    // Bulk orders - different colors by fulfillment type
    switch (fulfillmentType) {
      case 'pickup':
        return { bg: 'bg-amber-100', text: 'text-amber-800' };
      case 'patient_ship':
        return { bg: 'bg-violet-100', text: 'text-violet-800' };
      case 'bulk_ship':
      default:
        return { bg: 'bg-cyan-100', text: 'text-cyan-800' };
    }
  };

  const { bg: bgColor, text: textColor } = getColors();
  const Icon = orderType === 'patient' && shippingDestination !== 'clinic' ? User : Building2;

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${bgColor} ${textColor}`}>
      <Icon className="w-3 h-3 mr-1.5" />
      <span className="capitalize">{orderType}</span>
      <span className="mx-1.5 opacity-50">•</span>
      <span>{destinationLabel}</span>
    </span>
  );
};

/** Show product name for single-item orders, "N items" otherwise */
const formatItemSummary = (order: { itemCount: number; itemSummary?: Array<{ name: string; quantity: number }> }) => {
  if (order.itemCount === 1 && order.itemSummary?.[0]) {
    return order.itemSummary[0].name;
  }
  return `${order.itemCount} items`;
};

const ORDERS_PAGE_SIZE = 20;

const AdminOrders: React.FC = () => {
  const user = useUser();
  
  // State
  const [orders, setOrders] = useState<UnifiedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: ORDERS_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [counts, setCounts] = useState<OrderCounts>({ patient: 0, bulk: 0 });
  
  // Filters
  const [orderTypeFilter, setOrderTypeFilter] = useState<'all' | 'patient' | 'bulk'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentFilter, setPaymentFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Detail modal
  const [selectedOrder, setSelectedOrder] = useState<UnifiedOrderDetail | null>(null);
  // productId → isPeptide for the opened order, so the detail shows which
  // pharmacy (Blue Five Labs vs Greenstone Rx) fills it.
  const detailPeptideMap = useProductPeptideMap(
    (selectedOrder?.items ?? []).map((i) => String(i.productId))
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Status update
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [statusUpdateOrder, setStatusUpdateOrder] = useState<UnifiedOrder | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [retransmitLoading, setRetransmitLoading] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState({
    carrier: '',
    trackingNumber: '',
    trackingUrl: ''
  });

  // Payment modal for physician-billed orders
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState<UnifiedOrder | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'ach' | 'check' | 'cash' | 'other'>('check');
  const [transactionId, setTransactionId] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  // In-flight order edits
  const [showEditAddress, setShowEditAddress] = useState(false);
  const [showSwitchMode, setShowSwitchMode] = useState(false);
  const [showAddItems, setShowAddItems] = useState(false);

  // Invoice actions
  const [sendingPaymentLink, setSendingPaymentLink] = useState<string | null>(null);
  const [cancellingInvoice, setCancellingInvoice] = useState<string | null>(null);

  // Grouping
  const [groupByPhysician, setGroupByPhysician] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());


  // Access check
  if (!user || user.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access this page.</p>
        </div>
      </div>
    );
  }

  // Read search param from URL on load, auto-open detail if specified
  const [pendingAutoOpen, setPendingAutoOpen] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const search = params.get('search');
    if (search) {
      setSearchTerm(search);
      setPendingAutoOpen(search);
      // Clean up URL param
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Debounced search. A new search starts from page 1 (both updates batch into
  // one render, so only the page-1 request goes out).
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination(p => (p.page === 1 ? p : { ...p, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch orders (use larger page size when grouping so groups fill the page).
  // Not pagination.limit: the server echoes 100 back while grouped, which would
  // stick after ungrouping.
  const effectiveLimit = groupByPhysician ? 100 : ORDERS_PAGE_SIZE;
  const fetchSeq = useRef(0);

  const fetchOrders = useCallback(async () => {
    const mine = ++fetchSeq.current;
    try {
      setLoading(true);
      const response = await apiClient.getAdminOrders({
        page: pagination.page,
        limit: effectiveLimit,
        orderType: orderTypeFilter,
        status: statusFilter || undefined,
        invoiceStatus: paymentFilter || undefined,
        search: debouncedSearch || undefined
      });

      // A slower answer to an older filter/search must not overwrite a newer one.
      if (mine !== fetchSeq.current) return;
      if (response.success && response.data) {
        setOrders(response.data.data);
        setPagination(response.data.pagination);
        setCounts(response.data.counts);
      } else {
        toast.error(response.error || 'Failed to fetch orders');
      }
    } catch (error) {
      if (mine === fetchSeq.current) toast.error('Failed to fetch orders');
    } finally {
      if (mine === fetchSeq.current) setLoading(false);
    }
  }, [pagination.page, effectiveLimit, orderTypeFilter, statusFilter, paymentFilter, debouncedSearch]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Auto-open order detail when navigated from another page (e.g. fulfillment board).
  // Wait until the targeted search has actually been applied — otherwise the initial
  // fetch (with empty search) lands first, we fail to match, and clear pendingAutoOpen
  // before the targeted results arrive.
  useEffect(() => {
    if (!pendingAutoOpen) return;
    if (loading) return;
    if (debouncedSearch !== pendingAutoOpen) return;
    const match = orders.find(o => o.orderNumber === pendingAutoOpen);
    if (match) {
      fetchOrderDetail(match);
    } else {
      toast.error(`Order ${pendingAutoOpen} not found`);
    }
    setPendingAutoOpen(null);
  }, [orders, pendingAutoOpen, loading, debouncedSearch]);

  // Lock body scroll when detail modal is open
  useEffect(() => {
    if (showDetailModal) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showDetailModal]);

  // Fetch order detail
  const fetchOrderDetail = async (order: UnifiedOrder) => {
    try {
      setDetailLoading(true);
      const response = await apiClient.getAdminOrderDetail(order._id, order.orderType);

      if (response.success && response.data) {
        setSelectedOrder(response.data);
        setShowDetailModal(true);
      } else {
        toast.error(response.error || 'Failed to fetch order details');
      }
    } catch (error) {
      toast.error('Failed to fetch order details');
    } finally {
      setDetailLoading(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!statusUpdateOrder || !newStatus) return;

    try {
      setStatusUpdateLoading(true);
      const response = await apiClient.updateAdminOrderStatus(
        statusUpdateOrder._id,
        statusUpdateOrder.orderType,
        newStatus,
        newStatus === 'shipped' ? trackingInfo : undefined
      );

      if (response.success) {
        toast.success(`Order marked as ${newStatus}`);
        setShowStatusConfirmModal(false);
        setStatusUpdateOrder(null);
        setNewStatus('');
        setTrackingInfo({ carrier: '', trackingNumber: '', trackingUrl: '' });
        fetchOrders();
        
        // Update detail modal if open
        if (selectedOrder && selectedOrder._id === statusUpdateOrder._id) {
          fetchOrderDetail(statusUpdateOrder);
        }
      } else {
        toast.error(response.error || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  // Open status update modal
  const openStatusUpdateModal = (order: UnifiedOrder, status: string) => {
    setStatusUpdateOrder(order);
    setNewStatus(status);
    setShowStatusConfirmModal(true);
  };

  // Open payment modal for physician-billed orders
  const openPaymentModal = (order: UnifiedOrder) => {
    setPaymentOrder(order);
    setPaymentMethod('check');
    setTransactionId('');
    setPaymentNotes('');
    setShowPaymentModal(true);
  };

  // Handle marking invoice as paid
  const handleMarkAsPaid = async () => {
    if (!paymentOrder || !paymentOrder.invoiceId) return;

    try {
      setPaymentLoading(true);
      const response = await apiClient.markInvoiceAsPaid(
        paymentOrder.invoiceId,
        paymentMethod,
        transactionId || undefined,
        paymentNotes || undefined
      );

      if (response.success) {
        toast.success('Invoice marked as paid. Order submitted to pharmacy.');
        setShowPaymentModal(false);
        fetchOrders(); // Refresh orders list
      } else {
        toast.error(response.error || 'Failed to mark invoice as paid');
      }
    } catch (error) {
      toast.error('Failed to mark invoice as paid');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Send payment link for invoice
  const sendPaymentLink = async (invoiceId: string) => {
    try {
      setSendingPaymentLink(invoiceId);
      const response = await apiClient.post(`/invoices/${invoiceId}/send-payment-link`);
      if (response.success) {
        toast.success('Payment link sent successfully');
        // Refresh order detail if modal is open
        if (selectedOrder) {
          fetchOrderDetail({ _id: selectedOrder._id } as UnifiedOrder);
        }
      } else {
        toast.error(response.error || 'Failed to send payment link');
      }
    } catch (error: any) {
      console.error('Error sending payment link:', error);
      toast.error(error.message || 'Failed to send payment link');
    } finally {
      setSendingPaymentLink(null);
    }
  };

  // Cancel/void invoice
  const cancelInvoice = async (invoiceId: string) => {
    try {
      setCancellingInvoice(invoiceId);
      const response = await apiClient.put(`/invoices/${invoiceId}/cancel`);
      if (response.success) {
        toast.success('Invoice voided successfully');
        // Refresh order detail if modal is open
        if (selectedOrder) {
          fetchOrderDetail({ _id: selectedOrder._id } as UnifiedOrder);
        }
        // Also refresh the orders list
        fetchOrders();
      } else {
        toast.error(response.error || 'Failed to void invoice');
      }
    } catch (error: any) {
      console.error('Error voiding invoice:', error);
      toast.error('Failed to void invoice');
    } finally {
      setCancellingInvoice(null);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date (uses Eastern timezone)
  const formatOrderDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return formatDateTime(dateString);
  };

  // Check if can mark as shipped/delivered
  const canMarkAsShipped = (order: UnifiedOrder) => {
    return order.status !== 'draft' && order.status !== 'shipped' && order.status !== 'delivered';
  };

  const canMarkAsDelivered = (order: UnifiedOrder) => {
    return order.status !== 'draft' && order.status !== 'delivered';
  };

  // Get all available statuses for filter
  const getAllStatuses = () => {
    const patientStatuses = ['draft', 'awaiting_payment', 'submitted', 'processing', 'shipped', 'delivered', 'voided', 'cancelled', 'failed'];
    const bulkStatuses = ['draft', 'pending_approval', 'awaiting_payment', 'paid', 'processing', 'shipped', 'delivered', 'voided', 'cancelled'];

    if (orderTypeFilter === 'patient') return patientStatuses;
    if (orderTypeFilter === 'bulk') return bulkStatuses;
    return [...new Set([...patientStatuses, ...bulkStatuses])];
  };

  // Grouping logic
  const orderGroups = useMemo((): OrderGroup[] => {
    if (!groupByPhysician) return [];

    const groupMap = new Map<string, OrderGroup>();

    for (const order of orders) {
      const physicianId = order.physician?._id || 'unknown';
      const dateKey = formatDateForInput(order.createdAt);
      const key = `${physicianId}_${dateKey}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          key,
          physicianId,
          physicianName: order.physician?.name || 'Unknown',
          clinicName: order.physician?.clinicName,
          physicianEmail: order.physician?.email,
          amName: order.accountManager?.name,
          amCompanyName: order.accountManager?.companyName,
          amEmail: order.accountManager?.email,
          date: dateKey,
          orders: [],
          totalAmount: 0,
          totalItems: 0,
          statuses: [],
          orderTypes: []
        });
      }

      const group = groupMap.get(key)!;
      group.orders.push(order);
      group.totalAmount += order.totalAmount;
      group.totalItems += order.itemCount;
      if (!group.statuses.includes(order.status)) {
        group.statuses.push(order.status);
      }
      if (!group.orderTypes.includes(order.orderType)) {
        group.orderTypes.push(order.orderType);
      }
    }

    return Array.from(groupMap.values());
  }, [orders, groupByPhysician]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  // Reset expanded groups when orders change (page, filter, search)
  useEffect(() => {
    setExpandedGroups(new Set());
  }, [orders]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Orders</h1>
          <p className="mt-1 text-sm text-gray-500">
            Unified view of patient orders and bulk orders
          </p>
        </div>
        <button
          onClick={() => fetchOrders()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <RefreshCw className={`-ml-1 mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Orders</p>
              <p className="text-2xl font-semibold text-gray-900">{pagination.totalCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-emerald-100 rounded-md p-3">
              <User className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Patient Orders</p>
              <p className="text-2xl font-semibold text-gray-900">{counts.patient}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-cyan-100 rounded-md p-3">
              <Building2 className="h-6 w-6 text-cyan-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Bulk Orders</p>
              <p className="text-2xl font-semibold text-gray-900">{counts.bulk}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders, physicians, clinics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Order Type Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => { setOrderTypeFilter('all'); setPagination(p => ({ ...p, page: 1 })); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                orderTypeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => { setOrderTypeFilter('patient'); setPagination(p => ({ ...p, page: 1 })); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                orderTypeFilter === 'patient'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <User className="w-4 h-4 inline mr-1" />
              Patient
            </button>
            <button
              onClick={() => { setOrderTypeFilter('bulk'); setPagination(p => ({ ...p, page: 1 })); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                orderTypeFilter === 'bulk'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Building2 className="w-4 h-4 inline mr-1" />
              Bulk
            </button>
          </div>

          {/* Payment Status Filter */}
          <div className="w-40">
            <select
              value={paymentFilter}
              onChange={(e) => { setPaymentFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
              className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Payment</option>
              <option value="paid">Paid</option>
              <option value="sent">Unpaid</option>
              <option value="overdue">Overdue</option>
              <option value="voided">Voided</option>
              <option value="partial">Partial</option>
            </select>
          </div>

          {/* Fulfillment Status Filter */}
          <div className="w-40">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
              className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Fulfillment</option>
              {getAllStatuses().map(status => (
                <option key={status} value={status}>
                  {status.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          {/* Group by Clinic toggle */}
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Group by Clinic</span>
            <button
              type="button"
              onClick={() => { setGroupByPhysician(g => !g); setPagination(p => ({ ...p, page: 1 })); }}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                groupByPhysician ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  groupByPhysician ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter || paymentFilter ? 'Try adjusting your filters.' : 'Orders will appear here once created.'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop: Table view */}
            <div className="hidden md:block data-table-container">
              <div className="data-table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Type / Destination</th>
                      <th>Physician</th>
                      <th>Account Manager</th>
                      <th>Amount</th>
                      <th>Payment</th>
                      <th>Fulfillment</th>
                      <th>Created</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupByPhysician ? (
                      orderGroups.map((group) => (
                        group.orders.length === 1 ? (
                          // Single-order group: render as normal row
                          <tr
                            key={group.orders[0]._id}
                            className="clickable"
                            onClick={() => fetchOrderDetail(group.orders[0])}
                          >
                            <td className="cell-primary">
                              <div className="cell-stack">
                                <span className="cell-stack-value">{group.orders[0].orderNumber}</span>
                                <span className="cell-stack-label">{formatItemSummary(group.orders[0])}</span>
                              </div>
                            </td>
                            <td>
                              <OrderTypeBadge orderType={group.orders[0].orderType} fulfillmentType={group.orders[0].fulfillmentType} shippingDestination={group.orders[0].shippingDestination} />
                            </td>
                            <td>
                              <div className="cell-stack">
                                <span className="cell-stack-value cell-truncate">{group.physicianName}</span>
                                {group.clinicName && (
                                  <span className="cell-stack-label cell-truncate">{group.clinicName}</span>
                                )}
                              </div>
                            </td>
                            <td>
                              {group.orders[0].accountManager ? (
                                <div className="cell-stack">
                                  <span className="cell-stack-value cell-truncate">{group.orders[0].accountManager.name}</span>
                                  {group.orders[0].accountManager.companyName && (
                                    <span className="cell-stack-label cell-truncate">{group.orders[0].accountManager.companyName}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="cell-muted">—</span>
                              )}
                            </td>
                            <td className="cell-primary cell-mono">
                              {formatCurrency(group.orders[0].totalAmount)}
                            </td>
                            <td>
                              <PaymentStatusBadge invoiceStatus={group.orders[0].invoiceStatus} />
                            </td>
                            <td>
                              <div className="flex flex-col items-start gap-1">
                                <StatusBadge status={group.orders[0].status} orderType={group.orders[0].orderType} />
                                <PharmacyFailedBadge status={group.orders[0].pharmacySubmissionStatus} />
                              </div>
                            </td>
                            <td className="cell-secondary">
                              {formatOrderDate(group.orders[0].createdAt)}
                            </td>
                            <td className="cell-actions">
                              <div className="table-actions justify-end" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => fetchOrderDetail(group.orders[0])}
                                  className="table-action-btn table-action-btn-secondary"
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                                  View
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          // Multi-order group: summary row + expandable child rows
                          <React.Fragment key={group.key}>
                            <tr
                              className="cursor-pointer bg-gray-50 hover:bg-gray-100"
                              onClick={() => toggleGroup(group.key)}
                            >
                              <td className="cell-primary">
                                <div className="flex items-center gap-2">
                                  {expandedGroups.has(group.key) ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                  )}
                                  <span className="font-medium text-blue-700">{group.orders.length} orders</span>
                                </div>
                                <div className="text-xs text-gray-500 ml-6">{group.totalItems} items total</div>
                              </td>
                              <td>
                                <div className="flex gap-1 flex-wrap">
                                  {group.orderTypes.map(t => (
                                    <OrderTypeBadge key={t} orderType={t} />
                                  ))}
                                </div>
                              </td>
                              <td>
                                <div className="cell-stack">
                                  <span className="cell-stack-value cell-truncate font-semibold">{group.physicianName}</span>
                                  {group.clinicName && (
                                    <span className="cell-stack-label cell-truncate">{group.clinicName}</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                {group.amName ? (
                                  <div className="cell-stack">
                                    <span className="cell-stack-value cell-truncate">{group.amName}</span>
                                    {group.amCompanyName && (
                                      <span className="cell-stack-label cell-truncate">{group.amCompanyName}</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="cell-muted">—</span>
                                )}
                              </td>
                              <td className="cell-primary cell-mono font-semibold">
                                {formatCurrency(group.totalAmount)}
                              </td>
                              <td>
                                <div className="flex gap-1 flex-wrap">
                                  {[...new Set(group.orders.map(o => o.invoiceStatus))].map(s => (
                                    <PaymentStatusBadge key={s || 'na'} invoiceStatus={s} />
                                  ))}
                                </div>
                              </td>
                              <td>
                                <div className="flex gap-1 flex-wrap">
                                  {group.statuses.map(s => (
                                    <StatusBadge key={s} status={s} orderType="patient" />
                                  ))}
                                </div>
                              </td>
                              <td className="cell-secondary">
                                {formatOrderDate(group.orders[0].createdAt)}
                              </td>
                              <td className="cell-actions"></td>
                            </tr>
                            {expandedGroups.has(group.key) && group.orders.map((order) => (
                              <tr
                                key={order._id}
                                className="clickable bg-blue-100/50 border-l-4 border-l-blue-400"
                                onClick={() => fetchOrderDetail(order)}
                              >
                                <td className="cell-primary">
                                  <div className="ml-6">
                                    <div className="cell-stack">
                                      <span className="cell-stack-value">{order.orderNumber}</span>
                                      <span className="cell-stack-label">{formatItemSummary(order)}</span>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <OrderTypeBadge orderType={order.orderType} fulfillmentType={order.fulfillmentType} shippingDestination={order.shippingDestination} />
                                </td>
                                <td>
                                  <div className="cell-stack">
                                    <span className="cell-stack-value cell-truncate">{order.physician?.name || 'Unknown'}</span>
                                    {order.physician?.clinicName && (
                                      <span className="cell-stack-label cell-truncate">{order.physician.clinicName}</span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  {order.accountManager ? (
                                    <div className="cell-stack">
                                      <span className="cell-stack-value cell-truncate">{order.accountManager.name}</span>
                                      {order.accountManager.companyName && (
                                        <span className="cell-stack-label cell-truncate">{order.accountManager.companyName}</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="cell-muted">—</span>
                                  )}
                                </td>
                                <td className="cell-primary cell-mono">
                                  {formatCurrency(order.totalAmount)}
                                </td>
                                <td>
                                  <PaymentStatusBadge invoiceStatus={order.invoiceStatus} />
                                </td>
                                <td>
                                  <div className="flex flex-col items-start gap-1">
                                    <StatusBadge status={order.status} orderType={order.orderType} />
                                    <PharmacyFailedBadge status={order.pharmacySubmissionStatus} />
                                  </div>
                                </td>
                                <td className="cell-secondary">
                                  {formatOrderDate(order.createdAt)}
                                </td>
                                <td className="cell-actions">
                                  <div className="table-actions justify-end" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      onClick={() => fetchOrderDetail(order)}
                                      className="table-action-btn table-action-btn-secondary"
                                    >
                                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                                      View
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        )
                      ))
                    ) : (
                      orders.map((order) => (
                        <tr
                          key={order._id}
                          className="clickable"
                          onClick={() => fetchOrderDetail(order)}
                        >
                          <td className="cell-primary">
                            <div className="cell-stack">
                              <span className="cell-stack-value">{order.orderNumber}</span>
                              <span className="cell-stack-label">{formatItemSummary(order)}</span>
                            </div>
                          </td>
                          <td>
                            <OrderTypeBadge orderType={order.orderType} fulfillmentType={order.fulfillmentType} shippingDestination={order.shippingDestination} />
                          </td>
                          <td>
                            <div className="cell-stack">
                              <span className="cell-stack-value cell-truncate">{order.physician?.name || 'Unknown'}</span>
                              {order.physician?.clinicName && (
                                <span className="cell-stack-label cell-truncate">{order.physician.clinicName}</span>
                              )}
                              {order.physician?.email && (
                                <span className="cell-stack-label cell-truncate" title={order.physician.email}>
                                  {order.physician.email.length > 30
                                    ? order.physician.email.substring(0, 30) + '...'
                                    : order.physician.email}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            {order.accountManager ? (
                              <div className="cell-stack">
                                <span className="cell-stack-value cell-truncate">{order.accountManager.name}</span>
                                {order.accountManager.companyName && (
                                  <span className="cell-stack-label cell-truncate">{order.accountManager.companyName}</span>
                                )}
                                {order.accountManager.email && (
                                  <span className="cell-stack-label cell-truncate" title={order.accountManager.email}>
                                    {order.accountManager.email.length > 30
                                      ? order.accountManager.email.substring(0, 30) + '...'
                                      : order.accountManager.email}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="cell-muted">—</span>
                            )}
                          </td>
                          <td className="cell-primary cell-mono">
                            {formatCurrency(order.totalAmount)}
                          </td>
                          <td>
                            <PaymentStatusBadge invoiceStatus={order.invoiceStatus} />
                          </td>
                          <td>
                            <div className="flex flex-col items-start gap-1">
                              <StatusBadge status={order.status} orderType={order.orderType} />
                              <PharmacyFailedBadge status={order.pharmacySubmissionStatus} />
                            </div>
                          </td>
                          <td className="cell-secondary">
                            {formatOrderDate(order.createdAt)}
                          </td>
                          <td className="cell-actions">
                            <div className="table-actions justify-end" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => fetchOrderDetail(order)}
                                className="table-action-btn table-action-btn-secondary"
                              >
                                <Eye className="h-3.5 w-3.5 mr-1.5" />
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile: Card view */}
            <div className="md:hidden space-y-3 p-3">
              {groupByPhysician ? (
                orderGroups.map((group) => (
                  group.orders.length === 1 ? (
                    // Single-order group: render normal card
                    <div
                      key={group.orders[0]._id}
                      className="bg-white rounded-lg shadow p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => fetchOrderDetail(group.orders[0])}
                    >
                      <div className="flex flex-col space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center min-w-0 flex-1">
                            <Package className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                            <div className="min-w-0">
                              <h3 className="text-base font-medium text-gray-900">{group.orders[0].orderNumber}</h3>
                              <p className="text-sm text-gray-500">{formatItemSummary(group.orders[0])}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap justify-end">
                            <PaymentStatusBadge invoiceStatus={group.orders[0].invoiceStatus} />
                            <StatusBadge status={group.orders[0].status} orderType={group.orders[0].orderType} />
                            <PharmacyFailedBadge status={group.orders[0].pharmacySubmissionStatus} />
                          </div>
                        </div>
                        <div><OrderTypeBadge orderType={group.orders[0].orderType} fulfillmentType={group.orders[0].fulfillmentType} shippingDestination={group.orders[0].shippingDestination} /></div>
                        <div className="flex items-center">
                          <User className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900">{group.physicianName}</div>
                            {group.clinicName && <div className="text-sm text-gray-600 truncate">{group.clinicName}</div>}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Amount:</span>
                            <div className="font-semibold text-gray-900">{formatCurrency(group.orders[0].totalAmount)}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Created:</span>
                            <div className="font-medium text-gray-900">{formatOrderDate(group.orders[0].createdAt)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Multi-order group: collapsible card
                    <div key={group.key} className="bg-white rounded-lg shadow overflow-hidden">
                      <div
                        className="p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleGroup(group.key)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center min-w-0 flex-1">
                            <User className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                            <div className="min-w-0">
                              <h3 className="text-base font-semibold text-gray-900">{group.physicianName}</h3>
                              {group.clinicName && <p className="text-sm text-gray-600">{group.clinicName}</p>}
                            </div>
                          </div>
                          {expandedGroups.has(group.key) ? (
                            <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-sm">
                          <span className="font-semibold text-blue-700">{group.orders.length} orders</span>
                          <span className="text-gray-500">{formatOrderDate(group.orders[0].createdAt)}</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(group.totalAmount)}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {[...new Set(group.orders.map(o => o.invoiceStatus))].map(s => (
                            <PaymentStatusBadge key={s || 'na'} invoiceStatus={s} />
                          ))}
                          {group.statuses.map(s => (
                            <StatusBadge key={s} status={s} orderType="patient" />
                          ))}
                        </div>
                      </div>
                      {expandedGroups.has(group.key) && (
                        <div className="border-t border-gray-200 divide-y divide-gray-100">
                          {group.orders.map(order => (
                            <div
                              key={order._id}
                              className="p-4 pl-8 bg-blue-100/50 border-l-4 border-l-blue-400 cursor-pointer hover:bg-blue-100"
                              onClick={() => fetchOrderDetail(order)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">{order.orderNumber}</span>
                                    <OrderTypeBadge orderType={order.orderType} fulfillmentType={order.fulfillmentType} shippingDestination={order.shippingDestination} />
                                  </div>
                                  <p className="text-sm text-gray-500 mt-1">{formatItemSummary(order)}</p>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <span className="font-semibold text-gray-900">{formatCurrency(order.totalAmount)}</span>
                                  <div className="flex items-center gap-1 flex-wrap justify-end">
                                    <PaymentStatusBadge invoiceStatus={order.invoiceStatus} />
                                    <StatusBadge status={order.status} orderType={order.orderType} />
                                    <PharmacyFailedBadge status={order.pharmacySubmissionStatus} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                ))
              ) : (
                orders.map((order) => (
                  <div
                    key={order._id}
                    className="bg-white rounded-lg shadow p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => fetchOrderDetail(order)}
                  >
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center min-w-0 flex-1">
                          <Package className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                          <div className="min-w-0">
                            <h3 className="text-base font-medium text-gray-900">{order.orderNumber}</h3>
                            <p className="text-sm text-gray-500">{formatItemSummary(order)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <PaymentStatusBadge invoiceStatus={order.invoiceStatus} />
                          <StatusBadge status={order.status} orderType={order.orderType} />
                          <PharmacyFailedBadge status={order.pharmacySubmissionStatus} />
                        </div>
                      </div>
                      <div><OrderTypeBadge orderType={order.orderType} fulfillmentType={order.fulfillmentType} shippingDestination={order.shippingDestination} /></div>
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900">{order.physician?.name || 'Unknown'}</div>
                          {order.physician?.clinicName && (
                            <div className="text-sm text-gray-600 truncate">{order.physician.clinicName}</div>
                          )}
                          {order.physician?.email && (
                            <div className="text-sm text-gray-500 truncate" title={order.physician.email}>
                              {order.physician.email.length > 30 ? order.physician.email.substring(0, 30) + '...' : order.physician.email}
                            </div>
                          )}
                        </div>
                      </div>
                      {order.accountManager && (
                        <div className="flex items-center">
                          <Building2 className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900">{order.accountManager.name}</div>
                            {order.accountManager.companyName && (
                              <div className="text-sm text-gray-600 truncate">{order.accountManager.companyName}</div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Amount:</span>
                          <div className="font-semibold text-gray-900">{formatCurrency(order.totalAmount)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Created:</span>
                          <div className="font-medium text-gray-900">{formatOrderDate(order.createdAt)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); fetchOrderDetail(order); }}
                        className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                disabled={!pagination.hasPrevPage}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                disabled={!pagination.hasNextPage}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                  {' '}-{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.totalCount)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{pagination.totalCount}</span>
                  {' '}orders
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                    disabled={!pagination.hasPrevPage}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                    disabled={!pagination.hasNextPage}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDetailModal(false)} />

          <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col bg-white rounded-lg shadow-xl">
            {/* Modal Header */}
            <div className="flex-shrink-0 px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between rounded-t-lg">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    {selectedOrder.orderNumber}
                    <OrderTypeBadge orderType={selectedOrder.orderType} fulfillmentType={selectedOrder.fulfillmentType} shippingDestination={selectedOrder.shippingDestination} />
                    <PaymentStatusBadge invoiceStatus={selectedOrder.invoiceStatus} />
                    <StatusBadge status={selectedOrder.status} orderType={selectedOrder.orderType} />
                  </h3>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

            {/* Modal Body */}
            <div className="flex-1 px-6 py-4 overflow-y-auto">
                {/* Physician & Destination Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Physician</h4>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      <p className="font-medium text-gray-900">{selectedOrder.physician?.name || 'Unknown'}</p>
                      {selectedOrder.physician?.clinicName && (
                        <p className="text-sm text-gray-600">
                          <Building2 className="w-3 h-3 inline mr-1" />
                          {selectedOrder.physician.clinicName}
                        </p>
                      )}
                      {selectedOrder.physician?.clinicAddress && (
                        <div className="text-sm text-gray-500 flex items-start">
                          <MapPin className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                          <span>
                            {[
                              selectedOrder.physician.clinicAddress.street,
                              selectedOrder.physician.clinicAddress.street2,
                              selectedOrder.physician.clinicAddress.city,
                              selectedOrder.physician.clinicAddress.state,
                              selectedOrder.physician.clinicAddress.zipCode
                            ].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                      {selectedOrder.accountManager && (
                        <p className="text-sm text-gray-500 pt-1 border-t border-gray-200">AM: {selectedOrder.accountManager.name}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">
                      {selectedOrder.orderType === 'patient' ? 'Patient' : 'Destination'}
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-3">
                      {selectedOrder.orderType === 'patient' ? (
                        <div className="space-y-2">
                          <p className="font-medium text-gray-900">{selectedOrder.patient?.name || 'Unknown'}</p>
                          {selectedOrder.patient?.dateOfBirth && (
                            <p className="text-sm text-gray-600">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              DOB: {formatDateForInput(selectedOrder.patient.dateOfBirth)}
                            </p>
                          )}
                          {selectedOrder.patient?.phone && (
                            <p className="text-sm text-gray-600">
                              <Phone className="w-3 h-3 inline mr-1" />
                              {selectedOrder.patient.phone}
                            </p>
                          )}
                          {selectedOrder.patient?.email && (
                            <p className="text-sm text-gray-600">
                              <Mail className="w-3 h-3 inline mr-1" />
                              {selectedOrder.patient.email}
                            </p>
                          )}
                          {selectedOrder.patient?.address && (
                            <div className="text-sm text-gray-500 flex items-start pt-2 border-t border-gray-200 mt-2">
                              <MapPin className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                              <span>
                                {[
                                  selectedOrder.patient.address.street,
                                  selectedOrder.patient.address.street2,
                                  selectedOrder.patient.address.city,
                                  selectedOrder.patient.address.state,
                                  selectedOrder.patient.address.zipCode
                                ].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <p className="font-medium text-gray-900 capitalize">
                            {selectedOrder.fulfillmentType?.replace(/_/g, ' ') || 'Clinic Shipment'}
                          </p>
                          {/* Show clinic name for bulk orders */}
                          {selectedOrder.physician?.clinicName && (
                            <p className="text-sm text-gray-600 mt-1">
                              <Building2 className="w-3 h-3 inline mr-1" />
                              {selectedOrder.physician.clinicName}
                            </p>
                          )}
                          {/* Show shipping address if available with actual values, otherwise fall back to clinic address */}
                          {selectedOrder.shippingAddress && (selectedOrder.shippingAddress.street || selectedOrder.shippingAddress.city) ? (
                            <div className="text-sm text-gray-500 flex items-start mt-2">
                              <MapPin className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                              <span>
                                {[
                                  selectedOrder.shippingAddress.street,
                                  selectedOrder.shippingAddress.street2,
                                  selectedOrder.shippingAddress.city,
                                  selectedOrder.shippingAddress.state,
                                  selectedOrder.shippingAddress.zipCode
                                ].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          ) : selectedOrder.physician?.clinicAddress && (selectedOrder.physician.clinicAddress.street || selectedOrder.physician.clinicAddress.city) ? (
                            <div className="text-sm text-gray-500 flex items-start mt-2">
                              <MapPin className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                              <span>
                                {[
                                  selectedOrder.physician.clinicAddress.street,
                                  selectedOrder.physician.clinicAddress.street2,
                                  selectedOrder.physician.clinicAddress.city,
                                  selectedOrder.physician.clinicAddress.state,
                                  selectedOrder.physician.clinicAddress.zipCode
                                ].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 mt-1">No address available</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* In-flight edit actions */}
                {(() => {
                  const editabilityInput = {
                    status: selectedOrder.status,
                    pharmacyOrderId: (selectedOrder as any).pharmacyOrderId,
                    lastPharmacyStatus: (selectedOrder as any).lastPharmacyStatus,
                    physicianId: selectedOrder.physician?._id || '',
                    createdById: (selectedOrder as any).createdById,
                    orderType: selectedOrder.orderType === 'bulk' ? 'bulk_order' as const : 'order' as const,
                    pendingAddressChange: (selectedOrder as any).pendingAddressChange
                  };
                  const adminUser = { _id: user?._id || '', role: 'admin' };
                  const addrCheck = canEditOrderClient(editabilityInput, adminUser, 'address');
                  const addCheck = canEditOrderClient(editabilityInput, adminUser, 'add_items');
                  return (
                    <div className="flex flex-wrap gap-2 mb-4 -mt-2">
                      <button
                        onClick={() => addrCheck.ok && setShowEditAddress(true)}
                        disabled={!addrCheck.ok}
                        title={addrCheck.reason}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium border rounded-md ${
                          addrCheck.ok
                            ? 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200 cursor-pointer'
                            : 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed'
                        }`}
                      >
                        <MapPin className="h-4 w-4" /> Edit address
                      </button>
                      <button
                        onClick={() => addCheck.ok && setShowAddItems(true)}
                        disabled={!addCheck.ok}
                        title={addCheck.reason}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium border rounded-md ${
                          addCheck.ok
                            ? 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200 cursor-pointer'
                            : 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed'
                        }`}
                      >
                        <Package className="h-4 w-4" /> Add items
                      </button>
                      {(() => {
                        const dest = (selectedOrder as any).shippingDestination;
                        // Patient-shipped orders can't be switched. Otherwise
                        // any clinic/pickup order is switchable (admin gate +
                        // pending-action gate enforced server-side).
                        const switchable = dest === 'clinic' || dest === 'pickup';
                        return switchable ? (
                          <button
                            onClick={() => setShowSwitchMode(true)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium border rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border-indigo-200 cursor-pointer"
                          >
                            <MapPin className="h-4 w-4" />
                            {dest === 'pickup' ? 'Switch to ship' : 'Switch to pickup'}
                          </button>
                        ) : null;
                      })()}
                      {(!addrCheck.ok || !addCheck.ok) && (addrCheck.reason || addCheck.reason) && (
                        <div className="w-full text-xs text-gray-500 italic">
                          {addrCheck.reason || addCheck.reason}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {selectedOrder.orderType === 'patient' && !(selectedOrder as any).fulfillment && (
                  <div className="mb-4">
                    <OrderHoldPanel
                      order={selectedOrder as unknown as OrderHoldTarget}
                      isAdmin
                      onChanged={() => fetchOrderDetail(selectedOrder as unknown as UnifiedOrder)}
                    />
                  </div>
                )}

                {/* Items Table */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
<OrderQualificationResponses responses={selectedOrder.qualificationResponses} assessments={selectedOrder.programAssessments} />
                    <h4 className="text-sm font-medium text-gray-500">Order Items</h4>
                    <OrderPharmacyBadge
                      items={itemsWithPeptide(selectedOrder.items, detailPeptideMap)}
                      shipToState={selectedOrder.patient?.address?.state ?? selectedOrder.physician?.clinicAddress?.state}
                      withLabel
                      size="sm"
                    />
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedOrder.items.map((item, idx) => {
                          // Parse prescription data to get sig, refills, days supply, and pharmacy notes
                          let sig = '';
                          let refills = '';
                          let daysSupply = '';
                          let pharmacyNotes = '';
                          if (item.selectedVariants?.prescriptionData) {
                            try {
                              const prescriptionData = typeof item.selectedVariants.prescriptionData === 'string'
                                ? JSON.parse(item.selectedVariants.prescriptionData)
                                : item.selectedVariants.prescriptionData;
                              sig = prescriptionData.sig || '';
                              refills = prescriptionData.refills?.toString() || '';
                              daysSupply = prescriptionData.daysSupply?.toString() || '';
                              pharmacyNotes = prescriptionData.pharmacyNotes || '';
                            } catch {
                              // Ignore parse errors
                            }
                          }
                          
                          return (
                            <tr key={idx}>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{item.productName}</div>
                                  {sig && (
                                    <div className="text-xs text-gray-600 mt-1">
                                      <span className="font-medium">Sig:</span> {sig}
                                    </div>
                                  )}
                                  {(refills || daysSupply) && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {refills && <span>Refills: {refills}</span>}
                                      {refills && daysSupply && <span className="mx-1">•</span>}
                                      {daysSupply && <span>Days Supply: {daysSupply}</span>}
                                    </div>
                                  )}
                                  {pharmacyNotes && (
                                    <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1">
                                      <span className="font-medium">Notes:</span> {pharmacyNotes}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right align-top">{item.quantity}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 text-right align-top">{formatCurrency(item.unitPrice)}</td>
                              <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right align-top">{formatCurrency(item.totalPrice)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={3} className="px-4 py-2 text-sm text-gray-600 text-right">Subtotal</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{formatCurrency(selectedOrder.subtotal || selectedOrder.totalAmount)}</td>
                        </tr>
                        {(selectedOrder.shippingAmount ?? 0) > 0 && (
                          <tr>
                            <td colSpan={3} className="px-4 py-2 text-sm text-gray-600 text-right">Shipping</td>
                            <td className="px-4 py-2 text-sm text-gray-900 text-right">{formatCurrency(selectedOrder.shippingAmount || 0)}</td>
                          </tr>
                        )}
                        {(selectedOrder.shippingAmount ?? 0) === 0 &&
                          (selectedOrder as any).shippingDestination === 'clinic' && (
                          <tr>
                            <td colSpan={3} className="px-4 py-2 text-sm text-gray-500 text-right">Shipping</td>
                            <td className="px-4 py-2 text-xs text-gray-500 text-right">
                              {(selectedOrder as any).shippingBilledPerParcel ? 'billed per parcel at ship time' : 'free'}
                            </td>
                          </tr>
                        )}
                        {(selectedOrder.telemedNetworkFee ?? 0) > 0 && (
                          <tr>
                            <td colSpan={3} className="px-4 py-2 text-sm text-gray-600 text-right">Telemed network fee</td>
                            <td className="px-4 py-2 text-sm text-gray-900 text-right">{formatCurrency(selectedOrder.telemedNetworkFee || 0)}</td>
                          </tr>
                        )}
                        <tr className="border-t border-gray-200">
                          <td colSpan={3} className="px-4 py-2 text-sm font-medium text-gray-900 text-right">Total</td>
                          <td className="px-4 py-2 text-sm font-bold text-gray-900 text-right">{formatCurrency(selectedOrder.totalAmount)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Payment & Invoice Info */}
                <div className={`grid gap-6 mb-6 ${selectedOrder.paymentDetails && selectedOrder.invoiceId ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {/* Payment section - only show if there are actual payment details */}
                  {selectedOrder.paymentDetails && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                        <CreditCard className="w-4 h-4" />
                        Payment
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Method:</span> {selectedOrder.paymentDetails.method.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Status:</span> {selectedOrder.paymentDetails.status.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Amount:</span> {formatCurrency(selectedOrder.paymentDetails.amount)}
                        </p>
                        {(selectedOrder.paymentDetails.completedAt || selectedOrder.invoicePaidAt) && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Paid:</span> {formatOrderDate(selectedOrder.paymentDetails.completedAt || selectedOrder.invoicePaidAt)}
                          </p>
                        )}
                        {selectedOrder.paymentDetails.method === 'patient_pay' && selectedOrder.paymentDetails.status !== 'succeeded' && (
                          <button
                            onClick={async () => {
                              try {
                                const res = await apiClient.post(`/admin/orders/${selectedOrder._id}/refresh-payment`, {});
                                if (res.success && res.data?.updated) {
                                  alert(`Payment status updated: ${res.data.previousPaymentStatus} → ${res.data.currentPaymentStatus}`);
                                  fetchOrders();
                                } else if (res.success) {
                                  alert(`Payment status is current (Hyperswitch: ${res.data?.hyperswitchStatus})`);
                                } else {
                                  alert(`Failed: ${res.error}`);
                                }
                              } catch (err: any) {
                                alert(`Error: ${err.message}`);
                              }
                            }}
                            className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Refresh payment status
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Invoice Section - show for any order with an invoice */}
                  {selectedOrder.invoiceId && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        Invoice
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-3">
                        {selectedOrder.invoiceNumber ? (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <a
                                  href={`/admin/invoices?invoiceId=${selectedOrder.invoiceId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                                >
                                  {selectedOrder.invoiceNumber}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                selectedOrder.invoiceStatus === 'paid' ? 'bg-green-100 text-green-800' :
                                selectedOrder.invoiceStatus === 'voided' ? 'bg-gray-100 text-gray-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {selectedOrder.invoiceStatus}
                              </span>
                            </div>
                            {selectedOrder.invoicePaidAt && (
                              <p className="text-xs text-gray-500 mt-1">
                                Paid {formatOrderDate(selectedOrder.invoicePaidAt)}
                              </p>
                            )}

                            {/* Invoice Action Buttons */}
                            {selectedOrder.invoiceStatus !== 'paid' && selectedOrder.invoiceStatus !== 'voided' && (
                              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                                <a
                                  href={`/admin/invoices?invoiceId=${selectedOrder.invoiceId}&action=charge`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-blue-300 text-blue-700 bg-white hover:bg-blue-50"
                                >
                                  <CreditCard className="w-3 h-3 mr-1" />
                                  Charge
                                </a>
                                <button
                                  onClick={() => {
                                    setShowDetailModal(false);
                                    openPaymentModal(selectedOrder);
                                  }}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-green-300 text-green-700 bg-white hover:bg-green-50"
                                >
                                  <DollarSign className="w-3 h-3 mr-1" />
                                  Record
                                </button>
                                <button
                                  onClick={() => sendPaymentLink(selectedOrder.invoiceId!)}
                                  disabled={sendingPaymentLink === selectedOrder.invoiceId}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-purple-300 text-purple-700 bg-white hover:bg-purple-50 disabled:opacity-50"
                                >
                                  {sendingPaymentLink === selectedOrder.invoiceId ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Mail className="w-3 h-3 mr-1" />
                                  )}
                                  Send Link
                                </button>
                                <button
                                  onClick={() => cancelInvoice(selectedOrder.invoiceId!)}
                                  disabled={cancellingInvoice === selectedOrder.invoiceId}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                                >
                                  {cancellingInvoice === selectedOrder.invoiceId ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <XCircle className="w-3 h-3 mr-1" />
                                  )}
                                  Void
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No invoice</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Tracking Info */}
                {selectedOrder.trackingInfo && (selectedOrder.trackingInfo.carrier || selectedOrder.trackingInfo.trackingNumber || selectedOrder.trackingInfo.trackingUrl) && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                      <Truck className="w-4 h-4" />
                      Tracking Information
                    </h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {selectedOrder.trackingInfo.carrier && (
                          <div>
                            <p className="text-xs text-blue-600 font-medium">Carrier</p>
                            <p className="text-sm font-medium text-gray-900">{selectedOrder.trackingInfo.carrier}</p>
                          </div>
                        )}
                        {selectedOrder.trackingInfo.trackingNumber && (
                          <div>
                            <p className="text-xs text-blue-600 font-medium">Tracking Number</p>
                            <p className="text-sm font-medium text-gray-900 font-mono">{selectedOrder.trackingInfo.trackingNumber}</p>
                          </div>
                        )}
                        {selectedOrder.trackingInfo.trackingUrl && (
                          <div>
                            <p className="text-xs text-blue-600 font-medium">Tracking Link</p>
                            <a
                              href={selectedOrder.trackingInfo.trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 underline"
                            >
                              Track Package →
                            </a>
                          </div>
                        )}
                      </div>
                      {selectedOrder.trackingInfo.addedBy && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <p className="text-xs text-gray-500">
                            Added by <span className="font-medium">{selectedOrder.trackingInfo.addedBy.name}</span>
                            {selectedOrder.trackingInfo.addedAt && (
                              <> on {formatOrderDate(selectedOrder.trackingInfo.addedAt)}</>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Ship To Address (Transmitted to Pharmacy) */}
                {selectedOrder.transmittedAddress && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Ship To Address
                      {selectedOrder.orderType === 'patient' && selectedOrder.shippingDestination && (
                        <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          selectedOrder.shippingDestination === 'clinic'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {selectedOrder.shippingDestination === 'clinic' ? 'Clinic' : 'Patient'}
                        </span>
                      )}
                    </h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-sm text-gray-900">
                        {selectedOrder.transmittedAddress.street}
                        {selectedOrder.transmittedAddress.street2 && (
                          <>, {selectedOrder.transmittedAddress.street2}</>
                        )}
                      </div>
                      <div className="text-sm text-gray-900">
                        {selectedOrder.transmittedAddress.city}, {selectedOrder.transmittedAddress.state} {selectedOrder.transmittedAddress.zipCode}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pharmacy Submission Info */}
                {selectedOrder.pharmacySubmission && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                      <Pill className="w-4 h-4" />
                      Pharmacy Submission
                    </h4>
                    <div className={`rounded-lg p-4 border ${
                      selectedOrder.pharmacySubmission.status === 'fully_submitted'
                        ? 'bg-green-50 border-green-200'
                        : selectedOrder.pharmacySubmission.status === 'failed'
                        ? 'bg-red-50 border-red-200'
                        : selectedOrder.pharmacySubmission.status === 'partially_submitted'
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          selectedOrder.pharmacySubmission.status === 'fully_submitted'
                            ? 'bg-green-100 text-green-800'
                            : selectedOrder.pharmacySubmission.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : selectedOrder.pharmacySubmission.status === 'partially_submitted'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedOrder.pharmacySubmission.status.replace(/_/g, ' ')}
                        </span>
                        {selectedOrder.pharmacySubmission.lastUpdate && (
                          <span className="text-xs text-gray-500">
                            Last update: {formatOrderDate(selectedOrder.pharmacySubmission.lastUpdate)}
                          </span>
                        )}
                      </div>

                      {/* Failure reason (why the pharmacy rejected the order) */}
                      {selectedOrder.pharmacySubmission.status === 'failed' && selectedOrder.pharmacySubmission.failureReason && (
                        <div className="mb-3">
                          <p className="text-xs text-red-700 font-medium mb-1 flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Reason
                          </p>
                          <p className="text-sm text-red-800 bg-white px-2 py-1 rounded border border-red-200 whitespace-pre-wrap">
                            {selectedOrder.pharmacySubmission.failureReason}
                          </p>
                        </div>
                      )}

                      {/* Single Order ID (patient orders) */}
                      {selectedOrder.pharmacySubmission.pharmacyOrderId && (
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-1">Pharmacy Order ID</p>
                          <p className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200">
                            {selectedOrder.pharmacySubmission.pharmacyOrderId}
                          </p>
                        </div>
                      )}

                      {/* Multiple Order IDs (bulk orders) */}
                      {selectedOrder.pharmacySubmission.pharmacyOrderIds && selectedOrder.pharmacySubmission.pharmacyOrderIds.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-1">
                            Pharmacy Order IDs ({selectedOrder.pharmacySubmission.pharmacyOrderIds.length} orders)
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {selectedOrder.pharmacySubmission.pharmacyOrderIds.map((id, idx) => (
                              <span
                                key={idx}
                                className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200"
                              >
                                {id}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Not submitted message */}
                      {selectedOrder.pharmacySubmission.status === 'not_submitted' && (
                        <p className="text-sm text-gray-500">
                          This order has not been submitted to the pharmacy yet.
                        </p>
                      )}

                      {/* Re-transmit button for orders without pharmacy ID */}
                      {(!selectedOrder.pharmacySubmission.pharmacyOrderId &&
                        (!selectedOrder.pharmacySubmission.pharmacyOrderIds || selectedOrder.pharmacySubmission.pharmacyOrderIds.length === 0)) && (
                        <button
                          onClick={async () => {
                            if (!selectedOrder || retransmitLoading) return;
                            const confirmed = window.confirm(
                              `Are you sure you want to re-transmit order ${selectedOrder.orderNumber} to the pharmacy?`
                            );
                            if (!confirmed) return;

                            setRetransmitLoading(true);
                            try {
                              const result = await apiClient.retransmitOrderToPharmacy(
                                selectedOrder._id,
                                selectedOrder.orderType
                              );
                              if (result.success && result.data) {
                                toast.success(
                                  `Order re-transmitted successfully! ${
                                    result.data.pharmacyOrderId
                                      ? `Pharmacy ID: ${result.data.pharmacyOrderId}`
                                      : result.data.pharmacyOrderIds
                                        ? `${result.data.totalSubmitted} orders created`
                                        : ''
                                  }`
                                );
                                // Refresh order details
                                fetchOrderDetail(selectedOrder);
                              } else {
                                toast.error(result.error || 'Failed to re-transmit order');
                              }
                            } catch (error) {
                              console.error('Re-transmit error:', error);
                              toast.error('Failed to re-transmit order to pharmacy');
                            } finally {
                              setRetransmitLoading(false);
                            }
                          }}
                          disabled={retransmitLoading}
                          className="mt-3 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <RefreshCw className={`w-4 h-4 ${retransmitLoading ? 'animate-spin' : ''}`} />
                          {retransmitLoading ? 'Transmitting...' : 'Re-transmit to Pharmacy'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Timeline
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-sm font-medium">{formatOrderDate(selectedOrder.createdAt)}</p>
                    </div>
                    {selectedOrder.submittedAt && (
                      <div>
                        <p className="text-xs text-gray-500">Submitted</p>
                        <p className="text-sm font-medium">{formatOrderDate(selectedOrder.submittedAt)}</p>
                      </div>
                    )}
                    {(selectedOrder.paidAt || selectedOrder.invoicePaidAt) && (
                      <div>
                        <p className="text-xs text-gray-500">Paid</p>
                        <p className="text-sm font-medium">{formatOrderDate(selectedOrder.paidAt || selectedOrder.invoicePaidAt)}</p>
                      </div>
                    )}
                    {selectedOrder.shippedAt && (
                      <div>
                        <p className="text-xs text-gray-500">Shipped</p>
                        <p className="text-sm font-medium">{formatOrderDate(selectedOrder.shippedAt)}</p>
                      </div>
                    )}
                    {selectedOrder.deliveredAt && (
                      <div>
                        <p className="text-xs text-gray-500">Delivered</p>
                        <p className="text-sm font-medium">{formatOrderDate(selectedOrder.deliveredAt)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Override Section */}
                {(canMarkAsShipped(selectedOrder) || canMarkAsDelivered(selectedOrder)) && (
                  <div className="mt-4 pt-4 border-t border-orange-200 bg-orange-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-orange-800 mb-2 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Admin Override
                    </h4>
                    <p className="text-xs text-orange-600 mb-3">
                      These actions bypass the normal order workflow. Use with caution.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {canMarkAsShipped(selectedOrder) && (
                        <button
                          onClick={() => {
                            setShowDetailModal(false);
                            openStatusUpdateModal(selectedOrder, 'shipped');
                          }}
                          className="inline-flex items-center px-3 py-1.5 border border-orange-400 rounded-md text-sm font-medium text-orange-700 bg-white hover:bg-orange-100"
                        >
                          <Truck className="w-4 h-4 mr-1.5" />
                          Mark as Shipped
                        </button>
                      )}
                      {canMarkAsDelivered(selectedOrder) && (
                        <button
                          onClick={() => {
                            setShowDetailModal(false);
                            openStatusUpdateModal(selectedOrder, 'delivered');
                          }}
                          className="inline-flex items-center px-3 py-1.5 border border-orange-400 rounded-md text-sm font-medium text-orange-700 bg-white hover:bg-orange-100"
                        >
                          <CheckCircle className="w-4 h-4 mr-1.5" />
                          Mark as Delivered
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

            {/* Modal Footer */}
            <div className="flex-shrink-0 px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
              {/* Mark as Paid button for physician_billed orders awaiting payment */}
              {selectedOrder.orderType === 'patient' &&
               selectedOrder.paymentMethod === 'physician_billed' &&
               selectedOrder.status === 'awaiting_payment' &&
               selectedOrder.invoiceId && (
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    openPaymentModal(selectedOrder);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-green-600 rounded-md shadow-sm text-sm font-medium text-green-600 bg-white hover:bg-green-50"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Mark as Paid
                </button>
              )}
              <button
                onClick={() => setShowDetailModal(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Confirmation Modal */}
      {showStatusConfirmModal && statusUpdateOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowStatusConfirmModal(false)} />
            
            <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {newStatus === 'shipped' ? 'Mark Order as Shipped' : 'Mark Order as Delivered'}
                </h3>
              </div>

              <div className="px-6 py-4">
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to mark order <span className="font-semibold">{statusUpdateOrder.orderNumber}</span> as{' '}
                  <span className="font-semibold">{newStatus}</span>?
                </p>

                {/* Tracking Info for shipped status */}
                {newStatus === 'shipped' && (
                  <div className="space-y-3 mb-4">
                    <p className="text-sm font-medium text-gray-700">Tracking Information (optional)</p>
                    <input
                      type="text"
                      placeholder="Carrier (e.g., UPS, FedEx)"
                      value={trackingInfo.carrier}
                      onChange={(e) => setTrackingInfo(t => ({ ...t, carrier: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Tracking Number"
                      value={trackingInfo.trackingNumber}
                      onChange={(e) => setTrackingInfo(t => ({ ...t, trackingNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Tracking URL"
                      value={trackingInfo.trackingUrl}
                      onChange={(e) => setTrackingInfo(t => ({ ...t, trackingUrl: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                )}

                <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
                  <p className="text-sm text-yellow-700">
                    This action cannot be undone automatically.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowStatusConfirmModal(false);
                    setStatusUpdateOrder(null);
                    setNewStatus('');
                    setTrackingInfo({ carrier: '', trackingNumber: '', trackingUrl: '' });
                  }}
                  disabled={statusUpdateLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={statusUpdateLoading}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
                    newStatus === 'shipped'
                      ? 'bg-indigo-600 hover:bg-indigo-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {statusUpdateLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : newStatus === 'shipped' ? (
                    <Truck className="w-4 h-4 mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal for physician-billed orders */}
      {showPaymentModal && paymentOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowPaymentModal(false)} />

            <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Mark Invoice as Paid
                </h3>
              </div>

              <div className="px-6 py-4">
                <p className="text-sm text-gray-600 mb-4">
                  Mark invoice <span className="font-semibold">{paymentOrder.invoiceNumber || 'N/A'}</span> for order{' '}
                  <span className="font-semibold">{paymentOrder.orderNumber}</span> as paid?
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Amount: <span className="font-semibold">${paymentOrder.totalAmount.toFixed(2)}</span>
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="check">Check</option>
                      <option value="card">Card</option>
                      <option value="ach">ACH</option>
                      <option value="cash">Cash</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID (optional)</label>
                    <input
                      type="text"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      placeholder="Check number, transaction ID, etc."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                    <textarea
                      value={paymentNotes}
                      onChange={(e) => setPaymentNotes(e.target.value)}
                      placeholder="Add any notes about this payment..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> Once marked as paid, the order will automatically be submitted to the pharmacy for fulfillment.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentOrder(null);
                  }}
                  disabled={paymentLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkAsPaid}
                  disabled={paymentLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {paymentLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <DollarSign className="w-4 h-4 mr-2" />
                  )}
                  Confirm Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for detail fetch */}
      {detailLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg p-4 shadow-xl">
            <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}

      {/* In-flight address edit */}
      {showEditAddress && selectedOrder && (
        <EditAddressModal
          orderId={selectedOrder._id}
          orderType={selectedOrder.orderType === 'bulk' ? 'bulk_order' : 'order'}
          initial={selectedOrder.shippingAddress ? {
            street: selectedOrder.shippingAddress.street || '',
            street2: selectedOrder.shippingAddress.street2,
            city: selectedOrder.shippingAddress.city || '',
            state: selectedOrder.shippingAddress.state || '',
            zipCode: selectedOrder.shippingAddress.zipCode || ''
          } : (selectedOrder.patient?.address ? {
            street: selectedOrder.patient.address.street || '',
            street2: selectedOrder.patient.address.street2,
            city: selectedOrder.patient.address.city || '',
            state: selectedOrder.patient.address.state || '',
            zipCode: selectedOrder.patient.address.zipCode || ''
          } : undefined)}
          hasPharmacyOrder={!!(selectedOrder as any).pharmacyOrderId}
          isAdmin={true}
          destination={selectedOrder.shippingDestination}
          onClose={() => setShowEditAddress(false)}
          onSuccess={() => fetchOrderDetail({ _id: selectedOrder._id } as UnifiedOrder)}
        />
      )}

      {/* Delivery mode switch (ship ↔ pickup) */}
      {showSwitchMode && selectedOrder && (() => {
        const dest = (selectedOrder as any).shippingDestination as 'patient' | 'clinic' | 'pickup' | undefined;
        const currentMode: 'pickup' | 'ship' = dest === 'pickup' ? 'pickup' : 'ship';
        const existingAddress = selectedOrder.shippingAddress?.street ? {
          street: selectedOrder.shippingAddress.street,
          street2: selectedOrder.shippingAddress.street2,
          city: selectedOrder.shippingAddress.city || '',
          state: selectedOrder.shippingAddress.state || '',
          zipCode: selectedOrder.shippingAddress.zipCode || ''
        } : null;
        return (
          <SwitchDeliveryModeModal
            orderId={selectedOrder._id}
            orderType={selectedOrder.orderType === 'bulk' ? 'bulk_order' : 'order'}
            currentMode={currentMode}
            needsShippingAddress={!existingAddress}
            existingAddress={existingAddress}
            onClose={() => setShowSwitchMode(false)}
            onSuccess={() => fetchOrderDetail({ _id: selectedOrder._id } as UnifiedOrder)}
          />
        );
      })()}

      {/* In-flight add items */}
      {showAddItems && selectedOrder && (
        <AddItemsModal
          orderId={selectedOrder._id}
          orderNumber={selectedOrder.orderNumber}
          orderType={selectedOrder.orderType === 'bulk' ? 'bulk_order' : 'order'}
          physicianId={selectedOrder.physician?._id || ''}
          onClose={() => setShowAddItems(false)}
          onSuccess={() => fetchOrderDetail({ _id: selectedOrder._id } as UnifiedOrder)}
        />
      )}
    </div>
  );
};

export default AdminOrders;
